import { createRouter, createWebHashHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOnboardingStore } from '@/stores/onboarding'
import authApi from '@/api/auth'

const routes = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { public: true },
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/PricingView.vue'),
    meta: { public: true },
  },
  {
    path: '/invoice/:token',
    name: 'public-invoice',
    component: () => import('@/views/PublicInvoiceView.vue'),
    meta: { public: true },
  },
  {
    path: '/plan-limit',
    name: 'plan-limit',
    component: () => import('@/views/PlanLimitView.vue'),
  },
  {
    path: '/get-started',
    name: 'get-started',
    component: () => import('@/views/GetStartedView.vue'),
    meta: { ownerOnly: true },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
  },
  {
    path: '/customers',
    name: 'customers',
    component: () => import('@/views/CustomersView.vue'),
  },
  {
    path: '/customers/:id',
    name: 'customer-detail',
    component: () => import('@/views/CustomerDetailView.vue'),
  },
  {
    path: '/routes',
    name: 'routes',
    component: () => import('@/views/RoutesView.vue'),
  },
  {
    path: '/routes/:id',
    name: 'route-detail',
    component: () => import('@/views/RouteDetailView.vue'),
  },
  {
    path: '/invoices',
    name: 'invoices',
    component: () => import('@/views/InvoicesView.vue'),
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
  },
  {
    path: '/finances',
    name: 'finances',
    component: () => import('@/views/FinancesView.vue'),
    meta: { ownerOnly: true },
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/SupportView.vue'),
  },
  {
    path: '/team',
    name: 'team',
    component: () => import('@/views/TeamView.vue'),
    meta: { ownerOnly: true },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { superAdmin: true },
  },
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const employeeAllowedRoutes = ['routes', 'route-detail', 'settings', 'support', 'pricing', 'plan-limit']

  // Try to restore session on first load
  if (!auth.isAuthenticated && !auth._initialized) {
    auth._initialized = true
    await auth.fetchUser()
  }

  // Load onboarding state for owners
  if (auth.isAuthenticated && auth.user?.role === 'owner') {
    const onboarding = useOnboardingStore()
    if (!onboarding.loaded) onboarding.load(auth.user.id)
  }

  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login' }
  }

  if (auth.isAuthenticated && auth.user?.isOverCustomerLimit) {
    const allowed = ['plan-limit', 'customers', 'settings', 'support', 'pricing']
    if (!allowed.includes(to.name)) {
      return { name: 'plan-limit' }
    }
  }

  if (auth.isAuthenticated && auth.user?.role !== 'owner') {
    if (!employeeAllowedRoutes.includes(to.name)) {
      return { name: 'routes' }
    }
  }

  if (to.meta.ownerOnly && auth.user?.role !== 'owner') {
    return { name: 'routes' }
  }

  if (to.meta.superAdmin && !auth.user?.isSuperAdmin) {
    return { name: 'dashboard' }
  }

  const pricingUpgradeIntent = to.name === 'pricing' && to.query.intent === 'upgrade'
  if (['login', 'register', 'landing', 'pricing'].includes(to.name)
    && auth.isAuthenticated
    && !auth.user?.isOverCustomerLimit
    && !pricingUpgradeIntent) {
    if (auth.user?.role === 'owner') {
      const onboarding = useOnboardingStore()
      return onboarding.visible ? { name: 'get-started' } : { name: 'dashboard' }
    }
    return { name: 'routes' }
  }

  // Redirect owners with incomplete onboarding away from dashboard
  if (to.name === 'dashboard' && auth.isAuthenticated && auth.user?.role === 'owner') {
    const onboarding = useOnboardingStore()
    if (onboarding.visible) return { name: 'get-started' }
  }

  // If onboarding is done/skipped, don't allow direct nav to get-started
  if (to.name === 'get-started' && auth.isAuthenticated) {
    const onboarding = useOnboardingStore()
    if (!onboarding.visible) return { name: 'dashboard' }
  }
})

router.afterEach((to) => {
  authApi.trackPageView(to.fullPath).catch(() => { /* fire-and-forget */ })
})

export default router
